<template>
  <div class="watch" data-xl-gen="northstar">
    <div class="watch__page">
      <XlButton variant="ghost" size="sm" class="watch__back" @click="goBack">
        <IconBack color="currentColor" :size="15" />
        {{ backLabel }}
      </XlButton>

      <div v-if="loading" class="watch__loading">
        <XlSkeleton height="360px" />
        <XlSkeleton width="60%" height="28px" />
        <XlSkeleton width="35%" height="16px" />
      </div>

      <p v-else-if="loadError" class="watch__error" role="alert">{{ loadError }}</p>

      <p v-else-if="!hasAnything" class="watch__empty">
        This recording is not available to you. If you think it should be, ask your broker.
      </p>

      <template v-else>
        <video
          v-if="activePart?.video_url"
          ref="videoEl"
          class="watch__video"
          :src="activePart.video_url"
          controls
          controlsList="nodownload"
          preload="metadata"
          @loadeddata="playbackReady"
          @error="recoverPlayback"
        ></video>
        <p v-if="playbackFailed" class="watch__playback-error" role="alert">
          This part would not play. Try again in a moment, and tell your broker if it keeps happening.
        </p>

        <div v-if="parts.length > 1" class="watch__parts" role="tablist" aria-label="Parts of this recording">
          <button
            v-for="(part, i) in parts"
            :key="part.id"
            type="button"
            role="tab"
            class="watch__part"
            :class="{ 'is-active': part.id === activePartId }"
            :aria-selected="part.id === activePartId"
            @click="selectPart(part)"
          >
            {{ part.display_name || `Part ${i + 1}` }}
            <span>{{ formatDuration(part.duration_ms) }}</span>
          </button>
        </div>

        <h1 class="watch__title">{{ title }}</h1>
        <p class="watch__meta">{{ metaLine }}</p>

        <XlButton variant="ghost" size="sm" class="watch__share" @click="copyLink">
          <IconCopy color="currentColor" size="15" />
          {{ copied ? 'Link copied' : 'Copy link' }}
        </XlButton>

        <XlTabs v-if="hasRecap && hasTranscript" v-model="tab" :tabs="tabs" class="watch__tabs" />

        <XlCard v-if="shown === 'transcript'" padded class="watch__panel">
          <XlSearchField
            v-model="query"
            placeholder="Search the transcript"
            aria-label="Search the transcript"
            class="watch__search"
          />
          <p v-if="!shownEntries.length" class="watch__nothing">Nothing matches “{{ query }}”.</p>
          <div v-else class="watch__lines">
            <div v-for="row in shownEntries" :key="row.i" class="watch__line">
              <span class="watch__ts">{{ formatTimestamp(row.entry) }}</span>
              <div>
                <b>{{ speakerOf(row.entry) }}</b>
                <p>
                  <template v-for="(piece, p) in highlight(row.entry.text)" :key="p">
                    <mark v-if="piece.hit">{{ piece.text }}</mark>
                    <template v-else>{{ piece.text }}</template>
                  </template>
                </p>
              </div>
            </div>
          </div>
          <p class="watch__note">Auto-generated, and read through before it was published — it may still contain minor errors.</p>
        </XlCard>

        <!-- With no tabs to name it, the recap is named above its card (XN-4688, Greg's frame 1927:9085). -->
        <h2 v-if="shown === 'recap' && hasRecap && !hasTranscript" class="watch__label">AI recap</h2>
        <XlCard v-if="shown === 'recap' && hasRecap" padded class="watch__panel">
          <h2 v-if="recapTitle" class="watch__recap-title">{{ recapTitle }}</h2>
          <p v-if="recap.summary" class="watch__recap-summary">{{ recap.summary }}</p>

          <template v-if="keyTakeaways.length">
            <h3 class="watch__recap-heading">Key takeaways</h3>
            <ul class="watch__recap-list watch__recap-list--ticks">
              <li v-for="(point, i) in keyTakeaways" :key="`kt-${i}`">
                <span class="watch__tick" aria-hidden="true"><IconTick /></span>{{ point }}
              </li>
            </ul>
          </template>

          <template v-if="actionItems.length">
            <h3 class="watch__recap-heading">Action items</h3>
            <ul class="watch__recap-list">
              <li v-for="(item, i) in actionItems" :key="`ai-${i}`">
                {{ item.text }}
                <span v-if="item.owner" class="watch__recap-owner">{{ item.owner }}</span>
              </li>
            </ul>
          </template>

          <template v-if="chapters.length">
            <h3 class="watch__recap-heading">Chapters</h3>
            <div v-for="(chapter, i) in chapters" :key="`ch-${i}`" class="watch__chapter">
              <XlTag class="watch__chapter-ts">{{ formatClock(chapter.start_ms || 0) }}</XlTag>
              <div>
                <b>{{ chapter.title }}</b>
                <p v-if="chapter.summary">{{ chapter.summary }}</p>
              </div>
            </div>
          </template>

          <p class="watch__note">A written summary of the session, so you can read it instead of watching it all.</p>
        </XlCard>

        <template v-if="downloads.length">
          <h2 class="watch__downloads-title">Downloads</h2>
          <div class="watch__downloads">
            <a v-for="file in downloads" :key="file.label" class="watch__download" :href="file.url">
              <IconDownload color="currentColor" size="15" />
              {{ file.label }}
            </a>
          </div>
          <p class="watch__note">Your agency chose to let you keep these.</p>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup>
import axios from 'axios';
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import XlButton from '../../xl/XlButton.vue';
import XlCard from '../../xl/XlCard.vue';
import XlSearchField from '../../xl/XlSearchField.vue';
import { longDate } from '../../../helpers/usDate';
import XlSkeleton from '../../xl/XlSkeleton.vue';
import XlTabs from '../../xl/XlTabs.vue';
import XlTag from '../../xl/XlTag.vue';
import IconBack from '../../icons/IconBack.vue';
import IconCopy from '../../icons/IconCopy.vue';
import IconDownload from '../../icons/IconDownload.vue';
import IconTick from '../../icons/IconTick.vue';
import { useBackTo } from '../../../composables/useBackTo';

const route = useRoute();
const agendaEventId = computed(() => Number(route.params.agendaEventId));

const loading = ref(true);
const loadError = ref('');
const content = ref(null);
const videoEl = ref(null);
const activePartId = ref(null);
const tab = ref('transcript');
const query = ref('');
const copied = ref(false);
const playbackFailed = ref(false);
const retriedPartId = ref(null);

const parts = computed(() => content.value?.recordings ?? []);
const activePart = computed(() => parts.value.find(p => p.id === activePartId.value) || parts.value[0] || null);
const title = computed(() => content.value?.title || 'Session recording');
const recap = computed(() => content.value?.recap ?? {});
const chapters = computed(() => recap.value.chapters ?? []);
const keyTakeaways = computed(() => (recap.value.key_takeaways ?? []).filter(Boolean));
const actionItems = computed(() => (recap.value.action_items ?? []).filter(item => item?.text));
const recapTitle = computed(() => (recap.value.title && recap.value.title !== title.value ? recap.value.title : ''));
const hasRecap = computed(() => Boolean(
  recap.value.summary || chapters.value.length || keyTakeaways.value.length || actionItems.value.length,
));

// Each part carries its own transcript; the event-level one is the legacy shape
// for a session that was recorded in a single stretch.
const entries = computed(() => {
  const own = activePart.value?.transcript?.entries;
  if (Array.isArray(own) && own.length) return own;
  return parts.value.length <= 1 ? (content.value?.transcript?.entries ?? []) : [];
});
const speakerOverrides = computed(() => activePart.value?.transcript?.speaker_overrides ?? content.value?.transcript?.speaker_overrides ?? {});
const hasTranscript = computed(() => entries.value.length > 0);
const hasAnything = computed(() => parts.value.length > 0 || hasTranscript.value || hasRecap.value);
// The tab is a choice only while both exist: a part with no transcript shows the
// recap, whatever the last part was on (XN-4688).
const shown = computed(() => (hasTranscript.value ? tab.value : 'recap'));

const tabs = [
  { id: 'transcript', label: 'Transcript' },
  { id: 'recap', label: 'AI recap' },
];

const speakerOf = (entry) => {
  const id = entry.participant_identity ?? entry.speaker_id ?? entry.speaker;
  return (id && speakerOverrides.value?.[id]) || id || 'Speaker';
};

const formatClock = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;
const formatTimestamp = (entry) => formatClock(entry.session_time_ms ?? Math.max(0, (entry.timestamp || 0) * 1000));
const formatDuration = (ms) => {
  if (!ms || ms < 0) return '';
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
};

const metaLine = computed(() => {
  const first = parts.value.find(p => p.started_at)?.started_at;
  const total = parts.value.reduce((sum, p) => sum + (p.duration_ms || 0), 0);
  const bits = [];
  const day = longDate(first);
  if (day) bits.push(day);
  if (total > 0) bits.push(formatDuration(total));
  if (content.value?.host) bits.push(content.value.host);
  return bits.join(' · ');
});

// Every part that may be kept is listed at once, named. Offering only the part
// being watched made one pill mean a different file depending on the tab.
const downloads = computed(() => {
  const out = [];
  const many = parts.value.length > 1;
  parts.value.forEach((part, i) => {
    if (!part.download_url) return;
    const name = many ? `${part.display_name || `Part ${i + 1}`}.mp4` : 'Recording.mp4';
    out.push({ label: name, url: part.download_url });
  });
  if (content.value?.transcript?.download_url) out.push({ label: 'Transcript.txt', url: content.value.transcript.download_url });
  if (content.value?.recap_download_url) out.push({ label: 'AI recap.pdf', url: content.value.recap_download_url });
  return out;
});

const shownEntries = computed(() => {
  const q = query.value.trim().toLowerCase();
  return entries.value
    .map((entry, i) => ({ entry, i }))
    .filter(row => !q || (row.entry.text || '').toLowerCase().includes(q));
});

const highlight = (text = '') => {
  const q = query.value.trim();
  if (!q) return [{ text, hit: false }];
  const pieces = [];
  const haystack = text.toLowerCase();
  const needle = q.toLowerCase();
  let from = 0;
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, from)) {
    if (at > from) pieces.push({ text: text.slice(from, at), hit: false });
    pieces.push({ text: text.slice(at, at + needle.length), hit: true });
    from = at + needle.length;
  }
  if (from < text.length) pieces.push({ text: text.slice(from), hit: false });
  return pieces;
};

const selectPart = (part) => {
  activePartId.value = part.id;
  query.value = '';
  playbackFailed.value = false;
  if (videoEl.value) videoEl.value.currentTime = 0;
};

const playbackReady = () => {
  playbackFailed.value = false;
  retriedPartId.value = null;
};

// Playback URLs are presigned for five minutes, so a page left open on the
// transcript outlives them. Fetch a fresh set before telling anyone the
// recording is broken — but only once until the video loads again, or media
// that is genuinely unplayable would refetch on every error it raises.
const recoverPlayback = async () => {
  if (retriedPartId.value === activePartId.value) {
    playbackFailed.value = true;
    return;
  }
  retriedPartId.value = activePartId.value;

  const resumeAt = videoEl.value?.currentTime ?? 0;
  try {
    const { data } = await axios.get(`/api/agenda-events/${agendaEventId.value}/session-content`);
    if (!data?.data) throw new Error('no content');
    content.value = data.data;
    await nextTick();
    const el = videoEl.value;
    if (el) {
      el.addEventListener('loadedmetadata', () => { el.currentTime = resumeAt; }, { once: true });
      el.load();
    }
  } catch (e) {
    console.error(e);
    playbackFailed.value = true;
  }
};

// Back to wherever they came from: the Recordings tab, the session's page.
const { label: backLabel, go: goBack } = useBackTo({
  to: { path: '/hub/sessions', query: { tab: 'recordings' } },
  label: 'All recordings',
  list: /^\/hub\/sessions\?(.*&)?tab=recordings(&|$)/,
});

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(window.location.href);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2000);
  } catch (e) {
    console.error(e);
  }
};

onMounted(async () => {
  try {
    const { data } = await axios.get(`/api/agenda-events/${agendaEventId.value}/session-content`);
    content.value = data?.data ?? null;
    if (parts.value.length) activePartId.value = parts.value[0].id;
  } catch (e) {
    console.error(e);
    loadError.value = 'This recording could not be loaded.';
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.watch { min-height: 100%; }

.watch__page {
  box-sizing: border-box;
}

.watch__back { margin-bottom: 18px; }

.watch__loading { display: grid; gap: 14px; }

.watch__error,
.watch__empty {
  margin: 0;
  padding: 18px 20px;
  border-radius: 16px;
  border: 1px solid var(--xl-color-border);
  background: var(--xl-color-surface);
  font-size: 14.5px;
  color: var(--xl-color-secondary-text);
}

.watch__error { background: var(--xl-color-error-container, #FBEAEA); border-color: var(--xl-color-error); color: var(--xl-color-error); }

.watch__video {
  display: block;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 18px;
  background: linear-gradient(150deg, #3B2547, #1C1720);
}

.watch__playback-error {
  margin: 12px 0 0;
  padding: 11px 14px;
  border-radius: 12px;
  background: var(--xl-color-warning-container);
  color: var(--xl-color-warning);
  font-size: 13.5px;
}

.watch__parts { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }

.watch__part {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  padding: 8px 14px;
  border: 1px solid var(--xl-color-border);
  border-radius: 999px;
  background: #fff;
  font-family: var(--xl-font-family);
  font-size: 13.5px;
  font-weight: 600;
  color: var(--xl-color-secondary-text);
  cursor: pointer;
}

.watch__part span { font-weight: 400; font-size: 12.5px; color: var(--xl-color-outline); }
.watch__part.is-active { border-color: var(--xl-color-surface-tint); background: var(--xl-color-primary-container); color: var(--xl-color-on-primary-container); }

.watch__title {
  margin: 20px 0 4px;
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.03em;
  color: var(--xl-color-primary-text);
}

.watch__meta { margin: 0; font-size: 14.5px; color: var(--xl-color-outline); }

.watch__share { margin: 14px 0 6px; }

.watch__tabs { margin: 12px 0 18px; }

.watch__panel { margin-bottom: 20px; }

.watch__search { margin-bottom: 14px; }

.watch__lines { display: grid; }

.watch__line,
.watch__chapter {
  display: flex;
  gap: 14px;
  padding: 12px 0;
  border-bottom: 1px solid var(--xl-color-border-subtle);
}

.watch__line:last-of-type,
.watch__chapter:last-of-type { border-bottom: 0; }

.watch__ts {
  width: 46px;
  flex: 0 0 46px;
  padding-top: 2px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--xl-color-surface-tint);
  font-variant-numeric: tabular-nums;
}

.watch__line b,
.watch__chapter b { display: block; font-size: 14px; color: var(--xl-color-primary-text); }

/* Chapter titles read regular in the frame (XN-4688); speaker names stay bold. */
.watch__chapter b { font-weight: 400; }

.watch__line p,
.watch__chapter p {
  margin: 3px 0 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--xl-color-secondary-text);
}

.watch__line mark {
  background: var(--xl-color-primary-container);
  color: var(--xl-color-on-primary-container);
  border-radius: 3px;
}

.watch__nothing { margin: 0; padding: 14px 0; font-size: 14px; color: var(--xl-color-outline); }

.watch__recap-title {
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--xl-color-primary-text);
  text-wrap: balance;
}

.watch__recap-summary {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--xl-color-secondary-text);
}

/* XN-4688 (Greg's frame 1927:9085): the recap's own label, and sentence-case headings inside it. */
.watch__label {
  margin: 24px 0 12px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--xl-color-secondary-text);
}

.watch__recap-heading {
  margin: 22px 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: var(--xl-color-primary-text);
}

.watch__recap-list.watch__recap-list--ticks { padding-left: 0; list-style: none; gap: 18px; }
.watch__recap-list--ticks li { display: flex; align-items: center; gap: 12px; color: var(--xl-color-primary-text); }

.watch__tick {
  display: grid;
  place-items: center;
  flex: 0 0 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--xl-color-success-container, var(--xl-color-border));
  color: var(--xl-color-success, var(--xl-color-primary-text));
}

.watch__chapter-ts { flex: 0 0 auto; align-self: flex-start; }

.watch__recap-list {
  margin: 0;
  padding-left: 20px;
  display: grid;
  gap: 7px;
}

.watch__recap-list li {
  font-size: 14.5px;
  line-height: 1.6;
  color: var(--xl-color-secondary-text);
}

.watch__recap-owner {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--xl-color-primary-container);
  color: var(--xl-color-on-primary-container);
  font-size: 12px;
  white-space: nowrap;
}

.watch__note { margin: 14px 0 0; font-size: 12.5px; color: var(--xl-color-outline); }

.watch__downloads-title {
  margin: 8px 0 10px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: .1em;
  text-transform: uppercase;
  color: var(--xl-color-outline-variant);
}

.watch__downloads { display: flex; flex-wrap: wrap; gap: 8px; }

.watch__download {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 15px;
  border: 1px solid var(--xl-color-border);
  border-radius: 999px;
  background: #fff;
  font-size: 13.5px;
  font-weight: 600;
  color: var(--xl-color-primary-text);
  text-decoration: none;
}

.watch__download:hover { border-color: var(--xl-color-primary-fixed-dim); background: var(--xl-color-primary-container); }

@media (max-width: 600px) {
  .watch__title { font-size: 24px; }
}
</style>
